'use client'

import { Button } from '@/components/ui/Button'
import { myReferralCode } from '@/lib/referral-code'
import { NETWORKS, shareLink } from '@/lib/share'
import { track } from '@/lib/tracking'
import { useEffect, useState } from 'react'
import {
  RiBlueskyFill,
  RiCheckLine,
  RiFacebookFill,
  RiFileCopyLine,
  RiMailLine,
  RiRedditFill,
  RiShare2Line,
  RiTwitterXFill,
} from 'react-icons/ri'
import { BasicModal } from '../BasicModal'

// Share (2026-10-25). On a phone, the system share sheet: Messages, WhatsApp
// and the group chats are where news is passed on, and the old modal had none
// of them. On a computer, the modal: X, Facebook, Bluesky, Reddit, Email and
// Copy link. Every link says where it went and, for a signed-in reader,
// carries their referral code (lib/share).
//
// `kind` names what's shared, in the modal's heading and the link's
// utm_campaign: a story, a storyline or a topic (2026-10-24, Sam: it said
// "story" on every page). `variant` 'line' is the text link after a story's
// Left and right ("Share how each side covered this"), the same action.
const HEADING = {
  story: 'Share this story',
  storyline: 'Share this storyline',
  topic: 'Share this topic',
}
const ICONS = {
  x: RiTwitterXFill,
  facebook: RiFacebookFill,
  bluesky: RiBlueskyFill,
  reddit: RiRedditFill,
  mail: RiMailLine,
}

// A phone: the share sheet is there and the screen is touched. Desktop
// browsers have navigator.share too (macOS's sheet), but the modal's
// networks serve a computer better.
function usesShareSheet() {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    window.matchMedia?.('(pointer: coarse)').matches
  )
}

export default function ShareModalTrigger({
  url,
  quote,
  kind = 'story',
  variant = 'icon',
  label = 'Share',
  location = 'header',
}) {
  const [isModalOpen, setModalOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [code, setCode] = useState(null)

  // The reader's code, ahead of the tap (the share sheet must open inside
  // it).
  useEffect(() => {
    let live = true
    myReferralCode().then((c) => live && setCode(c))
    return () => {
      live = false
    }
  }, [])

  const linkFor = (source) => shareLink(url, { source, kind, code })

  const handleClose = () => {
    setModalOpen(false)
    setCopied(false) // Reset copied state when closing modal
  }

  const handleOpen = async () => {
    track('share_opened', { share_url: url, location })
    if (usesShareSheet()) {
      try {
        await navigator.share({ title: quote, url: linkFor('native') })
        track('share_clicked', { network: 'native', share_url: url })
      } catch (err) {
        // Closing the sheet is a choice, not a failure; anything else
        // falls back to the modal.
        if (err?.name !== 'AbortError') setModalOpen(true)
      }
      return
    }
    setModalOpen(true)
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(linkFor('copy'))
      track('share_link_copied', { share_url: url })
      setCopied(true)

      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <>
      {variant === 'line' ? (
        <button
          type="button"
          onClick={handleOpen}
          className="inline-flex items-center gap-1.5 font-sans text-sm text-muted-foreground underline decoration-foreground/40 decoration-[1.5px] underline-offset-4 transition-colors hover:text-foreground hover:decoration-foreground"
        >
          <RiShare2Line className="h-4 w-4" aria-hidden="true" />
          {label}
        </button>
      ) : (
        <button
          type="button"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group"
          onClick={handleOpen}
        >
          <RiShare2Line className="w-5 h-5" />
          <span className="byline">{label}</span>
        </button>
      )}

      <BasicModal
        isOpen={isModalOpen}
        onClose={handleClose}
        className="max-w-md"
      >
        <div className="p-6 space-y-6">
          <h2 className="font-serif text-2xl font-medium text-foreground pb-4 border-b-2 border-foreground">
            {HEADING[kind] || HEADING.story}
          </h2>

          <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {NETWORKS.map((n) => {
              const Icon = ICONS[n.id]
              return (
                <li key={n.id}>
                  <a
                    href={n.href(linkFor(n.id), quote)}
                    target={n.id === 'mail' ? undefined : '_blank'}
                    rel="noopener noreferrer"
                    onClick={() =>
                      track('share_clicked', { network: n.id, share_url: url })
                    }
                    className="flex flex-col items-center gap-1.5 rounded-md py-2 font-sans text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-foreground">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    {n.label}
                  </a>
                </li>
              )
            })}
          </ul>

          <Button variant="outline" onClick={handleCopyLink} className="w-full">
            {copied ? (
              <>
                <RiCheckLine className="w-4 h-4 mr-2 text-foreground" />
                Copied!
              </>
            ) : (
              <>
                <RiFileCopyLine className="w-4 h-4 mr-2" />
                Copy link
              </>
            )}
          </Button>
        </div>
      </BasicModal>
    </>
  )
}
